import type { LeadRecord } from "@/types";
import { CallLink } from "@/components/calling/CallLink";
import { dateLocale, withClock } from "@/lib/dateTime";

const Card = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <section className="flex flex-col gap-1 rounded-lg border border-border bg-card px-3.5 py-3">
    <h2 className="mb-1 text-[11.5px] font-semibold uppercase tracking-wide text-muted-foreground">{title}</h2>
    {children}
  </section>
);
const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex flex-col border-b border-border/60 py-1.5 last:border-b-0">
    <span className="text-[11.5px] text-muted-foreground">{label}</span>
    <span className="break-words text-[13px] font-medium">{children}</span>
  </div>
);
const when = (d?: string | null) => (d ? new Date(d).toLocaleString(dateLocale(), withClock({ day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })) : null);

/** Score ring, then the lead's contact, company and source details (empty fields are left out) */
export function LeadProperties({ lead, firstInquiry, lastInquiry, reasons = [] }: { lead: LeadRecord; firstInquiry?: string | null; lastInquiry?: string | null; reasons?: { label: string; points: number }[] }) {
  const score = Math.max(0, Math.min(100, Number(lead.score) || 0));
  const heat = lead.priority === "HOT" || score >= 60 ? "hot" : lead.priority === "COLD" || score < 30 ? "cold" : "warm";
  const C = 2 * Math.PI * 46;
  const contact: [string, React.ReactNode][] = [
    ["Mobile", lead.mobile && <CallLink type="LEAD" id={lead.id} field="mobile" number={lead.mobile} className="text-primary hover:underline">{lead.mobile}</CallLink>],
    ["Landline", lead.phone && <CallLink type="LEAD" id={lead.id} field="phone" number={lead.phone} className="text-primary hover:underline">{lead.phone}</CallLink>],
    ["Email", lead.email && <a href={`mailto:${lead.email}`} className="text-primary hover:underline">{lead.email}</a>],
    ["Job title", lead.job_title],
    ["Another number", lead.alternate_phone && <CallLink type="LEAD" id={lead.id} field="alternate_phone" number={lead.alternate_phone} className="text-primary hover:underline">{lead.alternate_phone}</CallLink>],
  ];
  const place = [lead.city, lead.state, lead.region].filter(Boolean).join(" · ");
  const company: [string, React.ReactNode][] = [
    ["Company", lead.company_name],
    ["Industry", lead.industry],
    ["Company size", lead.company_size],
    ["Website", lead.website && <a href={/^https?:\/\//.test(lead.website) ? lead.website : `https://${lead.website}`} target="_blank" rel="noreferrer" className="text-primary hover:underline">{lead.website.replace(/^https?:\/\//, "")}</a>],
    ["Location", place],
  ];
  const source: [string, React.ReactNode][] = [
    ["Source", lead.source],
    ["Campaign", lead.campaign],
    ["Referred by", lead.referred_by],
    ["First enquiry", when(firstInquiry)],
    ["Last enquiry", when(lastInquiry)],
  ];
  const shown = (rows: [string, React.ReactNode][]) => rows.filter(([, v]) => v);

  return (
    <div className="flex flex-col gap-3">
      <section className="flex items-center gap-3.5 rounded-lg border border-border bg-card px-3.5 py-3">
        <svg width="64" height="64" viewBox="0 0 108 108" role="img" aria-label={`Lead score ${score} out of 100`}>
          <circle cx="54" cy="54" r="46" fill="none" stroke="var(--border)" strokeWidth="9" />
          <circle cx="54" cy="54" r="46" fill="none" stroke={heat === "hot" ? "var(--gold)" : "var(--primary)"} strokeWidth="9" strokeLinecap="round" strokeDasharray={`${(score / 100) * C} ${C}`} transform="rotate(-90 54 54)" />
          <text x="54" y="63" textAnchor="middle" fontFamily="var(--font-mono)" fontSize="28" fontWeight="600" fill="var(--foreground)">
            {score}
          </text>
        </svg>
        <div className="flex flex-col">
          <span className="text-[13px] font-semibold">Lead score · {heat}</span>
          <span className="text-xs text-muted-foreground" title={reasons.map((r) => `${r.points > 0 ? "+" : ""}${r.points}  ${r.label}`).join("\n")}>
            {reasons.filter((r) => r.points > 0).slice(0, 3).map((r) => r.label).join(", ") || "Out of 100, from how they engage"}
            {reasons.some((r) => r.points < 0) && <span className="text-warning"> · {reasons.find((r) => r.points < 0)!.label.toLowerCase()}</span>}
          </span>
        </div>
      </section>
      {[["Contact", shown(contact)], ["Company", shown(company)], ["Source & campaign", shown(source)]].map(([title, rows]) =>
        (rows as [string, React.ReactNode][]).length > 0 ? (
          <Card key={title as string} title={title as string}>
            {(rows as [string, React.ReactNode][]).map(([label, value]) => (
              <Row key={label} label={label}>
                {value}
              </Row>
            ))}
          </Card>
        ) : null,
      )}
    </div>
  );
}
